import { useState, useEffect } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import ArtistLinking from "./ArtistLinking";

const EMAIL_KEY = "jbb_portal_email";

function normalizeEmail(e: string) {
  return e.trim().toLowerCase();
}

function getStoredEmail() {
  return localStorage.getItem(EMAIL_KEY) ?? "";
}

export default function LoginScreen() {
  const { signIn } = useAuthActions();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!getStoredEmail());
  const [forgot, setForgot] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [showLinking, setShowLinking] = useState(false);

  useEffect(() => {
    setEmail(getStoredEmail());
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setForgot(false);
    setLoading(true);
    try {
      const normalized = normalizeEmail(email);
      await signIn("password", {
        email: normalized,
        password,
        flow: mode === "signin" ? "signIn" : "signUp",
      });
      if (remember) localStorage.setItem(EMAIL_KEY, normalized);
      else localStorage.removeItem(EMAIL_KEY);

      // If signup, show artist linking
      if (mode === "signup") {
        setShowLinking(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(
        msg.toLowerCase().includes("invalid")
          ? "Email or password is incorrect."
          : msg || "Login failed",
      );
    } finally {
      setLoading(false);
    }
  }

  if (showLinking) {
    return (
      <ArtistLinking
        email={normalizeEmail(email)}
        onCancel={() => setShowLinking(false)}
        onLinked={() => setShowLinking(false)}
      />
    );
  }

  return (
    <>
      <div className="fixed inset-0 music-pattern-themed animate-music-drift" aria-hidden="true" />
      <main className="login-screen">
        <section className="login-card">
          <div className="login-brand">
            <img src="/johnsonboibeats-logo.png" alt="Johnsonboibeats" className="login-logo-mark" />
            <span className="login-wordmark">Johnsonboibeats</span>
            <strong>Artist Portal</strong>
          </div>

          <div className="auth-tabs">
            <button
              className={mode === "signin" ? "active" : ""}
              onClick={() => setMode("signin")}
              type="button"
            >
              Sign In
            </button>
            <button
              className={mode === "signup" ? "active" : ""}
              onClick={() => setMode("signup")}
              type="button"
            >
              Sign Up
            </button>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <label>
              <span>Email</span>
              <input
                autoComplete="email"
                className="theme-input"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="artist@email.com"
                type="email"
                value={email}
              />
            </label>
            <label>
              <span>Password</span>
              <input
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                className="theme-input"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                type="password"
                value={password}
              />
            </label>
            <div className="form-inline-actions">
              <label className="remember-row">
                <input
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  type="checkbox"
                />
                <span>Remember email</span>
              </label>
              {mode === "signin" ? (
                <button
                  className="text-button"
                  onClick={() => setForgot(true)}
                  type="button"
                >
                  Forgot password?
                </button>
              ) : null}
            </div>
            <button
              className="primary-action"
              disabled={loading || !email || !password}
              type="submit"
            >
              {loading
                ? (mode === "signin" ? "Signing in..." : "Creating account...")
                : (mode === "signin" ? "Sign in" : "Create account")}
            </button>
            {forgot ? (
              <p className="onboarding-note">
                Ask BJ for a temporary password. You can change it after signing in.
              </p>
            ) : null}

            {error ? <p className="form-error">{error}</p> : null}
          </form>
        </section>
      </main>
    </>
  );
}